'use client';

import React, { createContext, useCallback, useContext, useState } from 'react';
import OrderListSheet from './order-list-sheet';

interface OrderListSheetContextType {
  openSheet: (orderId?: string) => void;
  closeSheet: () => void;
  isOpen: boolean;
}

const OrderListSheetContext = createContext<
  OrderListSheetContextType | undefined
>(undefined);

export function OrderListSheetProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [orderId, setOrderId] = useState<string | null>(null);

  const openSheet = useCallback((orderId?: string) => {
    setOrderId(orderId ?? null);
    setIsOpen(true); // Always open the sheet when called
  }, []);

  const closeSheet = useCallback(() => {
    setIsOpen(false);

    // Dispatch a custom event when sheet is closed
    const event = new Event('sheet-closed');
    document.dispatchEvent(event);

    // We'll keep the current item during animation, then clear it
    setTimeout(() => {
      setOrderId(null);
    }, 300); // Adjust timing to match your animation duration
  }, []);

  return (
    <OrderListSheetContext.Provider
      value={{
        openSheet,
        closeSheet,
        isOpen,
      }}
    >
      {children}
      <OrderListSheet orderId={orderId} isOpen={isOpen} onClose={closeSheet} />
    </OrderListSheetContext.Provider>
  );
}

export function useOrderListSheet() {
  const context = useContext(OrderListSheetContext);
  if (context === undefined) {
    throw new Error(
      'useOrderListSheet must be used within a OrderListSheetProvider'
    );
  }
  return context;
}
